<script lang="ts" setup>
import { Model } from '@atabix/core'
import { useToggle } from '@vueuse/core'
import debounce from 'lodash-es/debounce'
import { onMounted, ref } from 'vue'

import type { Inputable } from '../../../../lib/types/Inputable'
import type { Testable } from '../../../../lib/types/Testable'
import AMultiSelect from '../../AMultiSelect/AMultiSelect.vue'
import ASingleSelect from '../../select/ASingleSelect.vue'
import type { AObjectSelectItem } from '../ASelectInput'

export interface AModelSelectProperties extends Testable, Inputable {
  type?: 'model-select'
  /** @see https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete#token_list_tokens */
  autocomplete?: boolean | string | undefined
  model: Model
  multiple: boolean
  label?: string
  ariaLabel?: string
  placeholder?: string
  limit?: number
  disabled?: boolean
  valueKey?: string
  nameKey?: string
  items?: never[]
}

const properties = withDefaults(defineProps<AModelSelectProperties>(), {
  id: undefined,
  type: 'model-select',
  class: undefined,
  name: undefined,
  label: undefined,
  limit: undefined,
  ariaLabel: undefined,
  autocomplete: undefined,
  placeholder: undefined,
  valueKey: 'id',
  nameKey: 'name',
  items: () => [],
})

const [_isLoading, setLoading] = useToggle(false)
const search = defineModel<string>('search', { default: '' })
const modelValue = defineModel<AObjectSelectItem[] | AObjectSelectItem>('modelValue', { required: true, default: [] })

const internalItems = ref<AObjectSelectItem[]>([])

async function fetch(): Promise<void> {
  setLoading(true)
  const filter = {
    search: search.value,
  }
  const limit = properties.limit
  try {
    const result = (await properties.model.filter(filter).page(1).limit(limit).all()) || []

    internalItems.value = result.map(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (item: Record<string, any>): AObjectSelectItem => ({
        value: item[properties.valueKey] as string,
        label: item[properties.nameKey] as string,
        item: item,
      }),
    )
  } finally {
    setLoading(false)
  }
}

function initializeModelValue(): void {
  if (!modelValue.value) return
  if (properties.multiple && Array.isArray(modelValue.value)) {
    modelValue.value = modelValue.value?.map(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (item: Record<string, any>): AObjectSelectItem => ({
        value: item[properties.valueKey] as string,
        label: item[properties.nameKey] as string,
        item: item,
      }),
    )
  }
}

function addModelValueToInternalItems(): void {
  if (!modelValue.value) return

  internalItems.value = Array.isArray(modelValue.value) ? [...internalItems.value, ...modelValue.value] : [...internalItems.value, modelValue.value]

  // Remove duplicates
  const uniqueItems = new Map<string, AObjectSelectItem>()
  internalItems.value.map((item) => {
    uniqueItems.set(item.value, item)
  })
  internalItems.value = [...uniqueItems.values()]
}

const debounceSearch = debounce(onSearch, 300)
async function onSearch(searchQuery: string): Promise<void> {
  search.value = searchQuery
  await fetch()
}

async function initialize(): Promise<void> {
  initializeModelValue()
  await fetch()
  addModelValueToInternalItems()
}

onMounted(initialize)
</script>

<template>
  <ASingleSelect v-if="!multiple && !Array.isArray(modelValue)" v-bind="$attrs" v-model="modelValue" ignore-search :items="internalItems">
    <template #item="forwardedProps"><slot name="item" v-bind="forwardedProps" /></template>
    <template #empty><slot name="empty" /></template>
  </ASingleSelect>
  <AMultiSelect
    v-if="multiple && Array.isArray(modelValue)"
    v-bind="$attrs"
    v-model="modelValue"
    :array-type="'object'"
    ignore-search
    :items="internalItems"
    @update:search="debounceSearch"
  >
    <template #item="forwardedProps"><slot name="item" v-bind="forwardedProps" /></template>
    <template #empty><slot name="empty" /></template>
  </AMultiSelect>
</template>

<style lang="scss">
.atabix-select-input {
  //
}
</style>
